import { NotificationCategory, NotificationMetadata } from '../../types/notification';
import { NOTIFICATION_CATEGORIES, NOTIFICATION_REQUEST_TYPES } from './constants';

/**
 * 카테고리별 제목 매핑
 */
interface CategoryContent {
  ko: string;
  en: string;
}

type CategoryMapper = {
  [key in NotificationCategory]?: CategoryContent | ((metadata?: NotificationMetadata) => CategoryContent);
};

const titleMap: CategoryMapper = {
  [NOTIFICATION_CATEGORIES.ONEDAY_REQUEST]: {
    ko: '원데이클래스 문의',
    en: 'One-day Class Inquiry'
  },
  [NOTIFICATION_CATEGORIES.LESSON_REQUEST]: {
    ko: '정규수업 신청 문의',
    en: 'Regular Lesson Request'
  },
  [NOTIFICATION_CATEGORIES.MODIFICATION_REQUEST]: (metadata?: NotificationMetadata) => ({
    ko: metadata?.requestType === NOTIFICATION_REQUEST_TYPES.CANCEL_LESSON
      ? '수업 일정 취소 요청'
      : '수업 일정 변경 요청',
    en: metadata?.requestType === NOTIFICATION_REQUEST_TYPES.CANCEL_LESSON
      ? 'Lesson Cancellation Request'
      : 'Lesson Schedule Change Request'
  }),
  [NOTIFICATION_CATEGORIES.MODIFICATION_APPROVED]: {
    ko: '수업 일정 변경 승인',
    en: 'Schedule Change Approved'
  },
  [NOTIFICATION_CATEGORIES.MODIFICATION_REJECTED]: {
    ko: '수업 일정 변경 반려',
    en: 'Schedule Change Rejected'
  },
  [NOTIFICATION_CATEGORIES.PRACTICE_BOOKED]: {
    ko: '연습실 예약 완료',
    en: 'Practice Room Booked'
  },
  [NOTIFICATION_CATEGORIES.PAYMENT_DUE]: {
    ko: '수강료 납부 안내',
    en: 'Payment Due Notice'
  },
  [NOTIFICATION_CATEGORIES.LESSON_REQUEST_APPROVED]: {
    ko: '정규수업 신청 승인',
    en: 'Lesson Request Approved'
  },
  [NOTIFICATION_CATEGORIES.LESSON_REQUEST_REJECTED]: {
    ko: '정규수업 신청 반려',
    en: 'Lesson Request Rejected'
  },
  [NOTIFICATION_CATEGORIES.ANNOUNCEMENT]: {
    ko: '전체 공지',
    en: 'Announcement'
  }
};

const messageMap: CategoryMapper = {
  [NOTIFICATION_CATEGORIES.ONEDAY_REQUEST]: {
    ko: '원데이클래스 문의가 접수되었습니다.',
    en: 'A one-day class inquiry has been received.'
  },
  [NOTIFICATION_CATEGORIES.LESSON_REQUEST]: {
    ko: '정규수업 신청 문의가 접수되었습니다.',
    en: 'A regular lesson request has been received.'
  },
  [NOTIFICATION_CATEGORIES.MODIFICATION_REQUEST]: (metadata?: NotificationMetadata) => ({
    ko: metadata?.requestType === NOTIFICATION_REQUEST_TYPES.CANCEL_LESSON
      ? '수업 일정 취소 요청이 접수되었습니다.'
      : '수업 일정 변경 요청이 접수되었습니다.',
    en: metadata?.requestType === NOTIFICATION_REQUEST_TYPES.CANCEL_LESSON
      ? 'A lesson cancellation request has been submitted.'
      : 'A schedule change request has been submitted.'
  }),
  [NOTIFICATION_CATEGORIES.MODIFICATION_APPROVED]: {
    ko: '수업 일정 변경이 승인되었습니다.',
    en: 'Your schedule change request has been approved.'
  },
  [NOTIFICATION_CATEGORIES.MODIFICATION_REJECTED]: {
    ko: '수업 일정 변경이 반려되었습니다.',
    en: 'Your schedule change request has been rejected.'
  },
  [NOTIFICATION_CATEGORIES.PRACTICE_BOOKED]: {
    ko: '연습실 예약이 완료되었습니다.',
    en: 'Your practice room booking is confirmed.'
  },
  [NOTIFICATION_CATEGORIES.PAYMENT_DUE]: {
    ko: '이번 달 수강료 납부 기한이 다가오고 있습니다.',
    en: 'Your monthly payment is due soon.'
  },
  [NOTIFICATION_CATEGORIES.LESSON_REQUEST_APPROVED]: {
    ko: '정규수업 신청이 승인되었습니다.',
    en: 'Your lesson request has been approved.'
  },
  [NOTIFICATION_CATEGORIES.LESSON_REQUEST_REJECTED]: {
    ko: '정규수업 신청이 반려되었습니다.',
    en: 'Your lesson request has been rejected.'
  }
};

/**
 * 알림 카테고리에 따른 제목 반환
 * @param category 알림 카테고리
 * @param metadata 알림 메타데이터 (선택적)
 * @param isForeigner 외국인 여부 (기본값: false)
 * @returns 카테고리에 맞는 제목 문자열
 */
export const getNotificationTitle = (
  category: NotificationCategory,
  metadata?: NotificationMetadata,
  isForeigner: boolean = false
): string => {
  const mapper = titleMap[category];

  if (!mapper) {
    return isForeigner ? 'Notification' : '알림';
  }

  const content = typeof mapper === 'function' ? mapper(metadata) : mapper;
  return isForeigner ? content.en : content.ko;
};

/**
 * 알림 카테고리에 따른 메시지 반환
 * @param category 알림 카테고리
 * @param metadata 알림 메타데이터 (선택적)
 * @param isForeigner 외국인 여부 (기본값: false)
 * @returns 카테고리에 맞는 메시지 문자열
 */
export const getNotificationMessage = (
  category: NotificationCategory,
  metadata?: NotificationMetadata,
  isForeigner: boolean = false
): string => {
  const mapper = messageMap[category];

  if (!mapper) {
    return '';
  }

  const content = typeof mapper === 'function' ? mapper(metadata) : mapper;
  return isForeigner ? content.en : content.ko;
};

/**
 * 알림에 제목/메시지가 직접 포함되어 있는지 확인
 * @param category 알림 카테고리
 * @returns 직접 메시지가 포함되는 카테고리인 경우 true
 */
export const hasCustomMessage = (category: NotificationCategory): boolean => {
  // 알림 객체에 직접 제목/메시지가 포함되는 카테고리
  return category === NOTIFICATION_CATEGORIES.ANNOUNCEMENT ||
         category === NOTIFICATION_CATEGORIES.PAYMENT_DUE;
};

/**
 * 카테고리가 선생님용 알림인지 확인
 * @param category 알림 카테고리
 * @returns 선생님용 알림인 경우 true
 */
export const isTeacherNotification = (category: NotificationCategory): boolean => {
  const teacherCategories: NotificationCategory[] = [
    NOTIFICATION_CATEGORIES.ONEDAY_REQUEST,
    NOTIFICATION_CATEGORIES.LESSON_REQUEST,
    NOTIFICATION_CATEGORIES.MODIFICATION_REQUEST,
  ];
  return teacherCategories.includes(category);
};

/**
 * 카테고리가 사용자용 알림인지 확인
 * @param category 알림 카테고리
 * @returns 사용자용 알림인 경우 true
 */
export const isUserNotification = (category: NotificationCategory): boolean => {
  const userCategories: NotificationCategory[] = [
    NOTIFICATION_CATEGORIES.MODIFICATION_APPROVED,
    NOTIFICATION_CATEGORIES.MODIFICATION_REJECTED,
    NOTIFICATION_CATEGORIES.PRACTICE_BOOKED,
    NOTIFICATION_CATEGORIES.PAYMENT_DUE,
    NOTIFICATION_CATEGORIES.LESSON_REQUEST_APPROVED,
    NOTIFICATION_CATEGORIES.LESSON_REQUEST_REJECTED,
  ];
  return userCategories.includes(category);
};
